import DatadogUtils from "@analytics/DatadogUtils";

type Timeline = Map<string, number>;

class PerformanceTimelineManager {
  private static readonly START_LABEL = "start";

  private timelines: Record<string, Timeline> = {};

  clear(id: string) {
    this.timelines[id]?.clear();
    delete this.timelines[id];
  }

  clearAll() {
    for (const id in this.timelines) {
      this.timelines[id]?.clear();
    }
    this.timelines = {};
  }

  getDurations(id: string) {
    const timeline = this.timelines[id];
    if (!timeline) {
      return null;
    }

    const from = timeline.get(PerformanceTimelineManager.START_LABEL);
    if (!from) {
      return null;
    }

    const result: Record<string, number> = {};
    for (const [label, time] of timeline.entries()) {
      if (label === PerformanceTimelineManager.START_LABEL) {
        continue;
      }
      result[`${label}_ms`] = Number((time - from).toFixed(2));
    }

    return result;
  }

  getManyDurations(ids: string[]) {
    const result: Record<string, Record<string, number>> = {};
    for (const id of ids) {
      const durations = this.getDurations(id);
      if (durations) {
        result[id] = durations;
      }
    }

    return result;
  }

  getRaw(id: string) {
    const timeline = this.timelines[id];
    if (!timeline) {
      return null;
    }
    const raw: Record<string, number> = {};
    for (const [label, time] of timeline.entries()) {
      raw[label] = time;
    }

    return raw;
  }

  getRawMany(ids: string[]) {
    const result: Record<string, Record<string, number>> = {};
    for (const id of ids) {
      const raw = this.getRaw(id);
      if (raw) {
        result[id] = raw;
      }
    }

    return result;
  }

  getStartLabelName() {
    return PerformanceTimelineManager.START_LABEL;
  }

  has(id: string) {
    return Boolean(this.timelines[id]);
  }

  logDurationsAndClear(ids: string[], logPrefix = ""): void {
    for (const id of ids) {
      const durations = this.getDurations(id);
      if (!durations) {
        continue;
      }

      const pathParts = window.location.pathname.split("/");
      const streamId = pathParts[pathParts.length - 1];

      DatadogUtils.info(
        `[AnalyticsPerformanceMeasure]: ${logPrefix ? `${logPrefix}.` : ""}timeline.${id}`,
        {
          durations,
          streamId,
        }
      );

      this.clear(id);
    }
  }

  mark(id: string, label: string) {
    if (!this.timelines[id]) {
      return;
    }
    this.timelines[id].set(label, performance.now());
  }

  markMany(ids: string[], labels: string[]) {
    const now = performance.now();
    for (const id of ids) {
      if (!this.timelines[id]) {
        continue;
      }

      labels.forEach((label) => {
        this.timelines[id].set(label, now);
      });
    }
  }

  removeMark(id: string, label: string) {
    const timeline = this.timelines[id];
    if (timeline) {
      timeline.delete(label);
    }
  }

  removeMarkMany(ids: string[], labels: string[]) {
    for (const id of ids) {
      const timeline = this.timelines[id];
      if (timeline) {
        labels.forEach((label) => {
          timeline.delete(label);
        });
      }
    }
  }

  start(id: string) {
    this.timelines[id] = new Map();
    this.timelines[id].set(
      PerformanceTimelineManager.START_LABEL,
      performance.now()
    );
  }
}

export const performanceTimelineManager = new PerformanceTimelineManager();
